import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Right } from '@linktivity/link-icons';
import { buildPlanPriceInfos } from '@link/share/hooks/productPageSelectors';
import { useBookingStore } from '../context';
import styles from './planPriceTable.module.css';

// Only ever opened by a click, so it loads then.
const PriceTableDialog = lazy(
  () => import('@link/share/business/PriceTableDialog')
);

interface Props {
  /** The day whose prices to show (YYYY-MM-DD); '' for none. */
  date: string;
}

/**
 * Every plan's unit prices on the date, behind a "Price table ›" button over
 * the corner of the plan picture. Nothing shows until the calendar has priced
 * that day.
 */
export const PlanPriceTable = observer(function PlanPriceTable({
  date
}: Props) {
  const store = useBookingStore();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Kept mounted once opened, so closing plays the dialog's animation.
  const [opened, setOpened] = useState(false);
  const day = date
    ? store.calendarDays.find(item => item.date === date)
    : undefined;
  const planPriceInfos = buildPlanPriceInfos(store.plans, day);
  if (!planPriceInfos.length) return null;
  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={() => {
          setOpen(true);
          setOpened(true);
        }}
      >
        {t('share.planCard.viewPriceTable')}
        <Right className={styles.icon} />
      </button>
      {opened && (
        <Suspense fallback={null}>
          <PriceTableDialog
            visible={open}
            date={date}
            planPriceInfos={planPriceInfos}
            currency={store.currency}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
});
